import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { MapPin, Star } from "lucide-react";

const CALGARY = [51.0447, -114.0719];

export default function MapView() {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const markers = useRef([]);
  const [businesses, setBusinesses] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/businesses").then((r) => setBusinesses(r.data));
  }, []);

  // init map once
  useEffect(() => {
    if (mapObj.current || !window.L || !mapRef.current) return;
    const L = window.L;
    mapObj.current = L.map(mapRef.current, { scrollWheelZoom: true }).setView(CALGARY, 12);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(mapObj.current);
  }, []);

  // render markers when businesses load
  useEffect(() => {
    const L = window.L;
    if (!L || !mapObj.current) return;
    markers.current.forEach((m) => m.remove());
    markers.current = [];
    const withCoords = businesses.filter((b) => b.lat && b.lng);
    const icon = L.divIcon({
      className: "",
      html: `<div style="background:#F97316;width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,.3);"></div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 30],
    });
    withCoords.forEach((b) => {
      const m = L.marker([b.lat, b.lng], { icon }).addTo(mapObj.current);
      const el = document.createElement("div");
      el.style.minWidth = "160px";
      const name = document.createElement("strong");
      name.textContent = b.name;
      const addr = document.createElement("div");
      addr.style.cssText = "color:#64748b;font-size:12px;margin:2px 0 6px";
      addr.textContent = b.address || "";
      const link = document.createElement("button");
      link.textContent = "View profile →";
      link.style.cssText = "color:#F97316;font-weight:600;font-size:13px;background:none;border:none;padding:0;cursor:pointer";
      link.onclick = () => navigate(`/business/${b.id}`);
      el.appendChild(name);
      el.appendChild(addr);
      el.appendChild(link);
      m.bindPopup(el);
      m.on("click", () => setActive(b.id));
      markers.current.push(m);
    });
    if (withCoords.length) {
      const group = L.featureGroup(markers.current);
      mapObj.current.fitBounds(group.getBounds().pad(0.2));
    }
  }, [businesses]);

  const focus = (b) => {
    setActive(b.id);
    if (mapObj.current && b.lat && b.lng) {
      mapObj.current.setView([b.lat, b.lng], 15, { animate: true });
      const m = markers.current.find((mk) => {
        const ll = mk.getLatLng();
        return Math.abs(ll.lat - b.lat) < 1e-6 && Math.abs(ll.lng - b.lng) < 1e-6;
      });
      m?.openPopup();
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 min-h-[70vh]">
      <h1 className="font-display text-4xl font-semibold tracking-tight">Explore the map</h1>
      <p className="text-slate-500 mt-1 mb-6">Discover local businesses across Calgary.</p>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 order-2 lg:order-1">
          <div ref={mapRef} data-testid="leaflet-map" className="w-full h-[420px] lg:h-[600px] rounded-3xl overflow-hidden border border-slate-200 shadow-sm z-0" />
        </div>
        <div className="order-1 lg:order-2 space-y-3 lg:max-h-[600px] lg:overflow-y-auto no-scrollbar">
          {businesses.map((b) => (
            <button
              key={b.id}
              data-testid={`map-list-${b.id}`}
              onClick={() => focus(b)}
              className={`w-full text-left flex gap-3 bg-white rounded-2xl border p-3 transition-all hover:shadow-md ${active === b.id ? "border-orange-400 ring-1 ring-orange-200" : "border-slate-100"}`}
            >
              <img src={b.logo || b.cover} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold truncate">{b.name}</p>
                <p className="text-xs text-slate-500 flex items-center gap-1 truncate"><MapPin className="w-3 h-3 shrink-0" /> {b.address}</p>
                <p className="text-xs text-slate-600 flex items-center gap-1 mt-0.5"><Star className="w-3 h-3 fill-orange-400 text-orange-400" /> {b.rating?.toFixed(1)} · {b.categories?.[0]}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
